'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import { useId, useRef } from 'react';
import { cn } from '../../lib/cn.js';
import styles from './Tabs.module.css';
/** ARIA APG `tablist` pattern, overflow-x scroll on mobile. Renders only the
 * tab strip — the panel is owned by the consumer via `role="tabpanel"` +
 * matching id. */
export function Tabs({ items, value, onChange, label, loading = false, variant = 'underline', className, }) {
    const baseId = useId();
    const tabRefs = useRef(new Map());
    function focusAndSelect(item) {
        onChange(item.value);
        tabRefs.current.get(item.value)?.focus();
    }
    function isSelectable(item) {
        return item !== undefined && !item.disabled && !loading;
    }
    // ARIA APG Tabs pattern (ux.md "follow the ARIA APG pattern rather than
    // re-inventing keyboard behavior"): Left/Right (Home/End) move focus AND
    // activate the next enabled tab — required because the roving `tabIndex`
    // below removes every non-active tab from the natural Tab order, so
    // without this handler they would be permanently keyboard-unreachable.
    function onKeyDown(event, index) {
        const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
        const isHome = event.key === 'Home';
        const isEnd = event.key === 'End';
        if (step === 0 && !isHome && !isEnd)
            return;
        event.preventDefault();
        if (isHome || isEnd) {
            const ordered = isHome ? items : [...items].reverse();
            const target = ordered.find((item) => isSelectable(item));
            if (target)
                focusAndSelect(target);
            return;
        }
        // Walk the strip in `step` direction, wrapping around, until an enabled
        // tab is found (or every tab has been visited once).
        for (let i = 1; i <= items.length; i += 1) {
            const candidateIndex = (index + step * i + items.length * items.length) % items.length;
            const candidate = items[candidateIndex];
            if (isSelectable(candidate)) {
                focusAndSelect(candidate);
                return;
            }
        }
    }
    const isSegmented = variant === 'segmented';
    return (_jsx("div", { role: "tablist", "aria-label": label, className: cn(styles['tablist'], isSegmented && styles['segmented'], className), children: items.map((item, index) => {
            const active = item.value === value;
            return (_jsx("button", { ref: (node) => {
                    if (node)
                        tabRefs.current.set(item.value, node);
                    else
                        tabRefs.current.delete(item.value);
                }, type: "button", role: "tab", id: `${baseId}-tab-${item.value}`, "aria-controls": `${baseId}-panel-${item.value}`, "aria-selected": active, tabIndex: active ? 0 : -1, disabled: item.disabled || loading, className: cn(styles['tab'], isSegmented ? styles['segmentedTab'] : styles['underlineTab'], active && (isSegmented ? styles['segmentedActive'] : styles['active'])), onClick: () => onChange(item.value), onKeyDown: (event) => onKeyDown(event, index), children: item.label }, item.value));
        }) }));
}
